import { Injectable } from "@angular/core";
import { DateTime, Settings } from "luxon";
import { UtilsService } from "src/app/shared/services/utils.service";
import { Adapter } from "../adapter";
import { Alert, AlertType } from "./alert.model";
import { Gateway } from "./gateway.model";
import { Meter, METER_3P_TYPES, METER_DRAC_TYPES } from "./meter.model";
import { Model } from "./model";
import { User } from "./user.model";

export enum TargetType {
  meter = "meter",
  gateway = "gateway",
  place = "place",
}

export enum RuleType {
  smart = "smart", // group of daily_profile, flow_profile
  leak = "leak", // group of leak_3p, burst_3p, t_from_pause_30_min, t_from_pause_60_min
  periodicity = "periodicity",
  duration = "duration",
  volume = "volume",
  daily_volume = "daily_volume",
  monthly_volume = "monthly_volume",
  low_capacitor = "low_capacitor",
  meter_offline = "meter_offline",
  gateway_offline = "gateway_offline",
}

export const dracRuleTypes: string[] = [
  RuleType.smart,
  RuleType.periodicity,
  RuleType.duration,
  RuleType.volume,
];

export enum RuleCategory {
  smart = "smart",
  leak = "leak", // category of leak, periodicity
  equipment = "equipment", // category of low_capacitor, meter_offline, gateway_offline
  period = "period", // category of daily_volume, monthly_volume
  threshold = "threshold", // category of volume, duration
}

export const SYSTEM_ALERT_DEFINITIONS = {
  daily_profile: {
    value: 4, //TODO ivan
    target: "meter",
    maxValue: 10,
    meterTypes: METER_DRAC_TYPES,
    feature: "smart_alerts",
    alertType: AlertType.daily_profile,
  },
  flow_profile: {
    value: 1, //TODO ivan
    maxValue: 2.5,
    target: "meter",
    meterTypes: METER_DRAC_TYPES,
    feature: "smart_alerts",
    alertType: AlertType.flow_profile,
  },
  periodicity: {
    value: null,
    target: "meter",
    meterTypes: METER_DRAC_TYPES,
    feature: "smart_alerts",
    alertType: AlertType.periodicity,
  },
  t_from_pause_30_min: {
    value: 15,
    target: "meter",
    meterTypes: METER_DRAC_TYPES,
    feature: "smart_alerts",
    alertType: AlertType.t_from_pause_30_min,
  },
  t_from_pause_60_min: {
    value: 18,
    target: "meter",
    meterTypes: METER_DRAC_TYPES,
    feature: "smart_alerts",
    alertType: AlertType.t_from_pause_60_min,
  },
  leak_3p: {
    value: null,
    target: "meter",
    meterTypes: METER_3P_TYPES,
    feature: null,
    alertType: AlertType.leak_3p,
  },
  burst_3p: {
    value: null,
    target: "meter",
    meterTypes: METER_3P_TYPES,
    feature: null,
    alertType: AlertType.burst_3p,
  },
};

export const minValue = {
  [AlertType.volume]: 40,
  [AlertType.daily_volume]: 40,
  [AlertType.monthly_volume]: 1000,
  [AlertType.duration]: 900,
  [AlertType.flow_profile]: 0.5,
  [AlertType.daily_profile]: 2,
  [AlertType.burst_3p]: null,
  [AlertType.leak_3p]: null,
  [AlertType.low_capacitor]: null,
  [AlertType.gateway_offline]: null,
  [AlertType.meter_offline]: null,
  [AlertType.periodicity]: null,
  [AlertType.t_from_pause_30_min]: null,
  [AlertType.t_from_pause_60_min]: null,
};

export const maxValue = {
  [AlertType.volume]: 5000,
  [AlertType.daily_volume]: 1000000,
  [AlertType.monthly_volume]: 50000000,
  [AlertType.duration]: 14400,
  [AlertType.flow_profile]: 2.5,
  [AlertType.daily_profile]: 10,
  [AlertType.burst_3p]: null,
  [AlertType.leak_3p]: null,
  [AlertType.low_capacitor]: null,
  [AlertType.gateway_offline]: null,
  [AlertType.meter_offline]: null,
  [AlertType.periodicity]: null,
  [AlertType.t_from_pause_30_min]: null,
  [AlertType.t_from_pause_60_min]: null,
};

export interface AlertDefinitionData {
  targetLabel: string;
  ruleLabel: string;
}

export const SMART_RULE_ID = -1;
export const LEAK_RULE_ID = -2;
export const PERIODICITY_RULE_ID = -3;

export class AlertDefinition implements Model {
  public targetLabel: string; // ex: JA2305543, Grenoble, Tous (need meters, places)
  public ruleLabel: string; // ex: Conso continue > 50L (need user & utils)

  public get targetType(): TargetType {
    if (this.type === AlertType.gateway_offline) {
      return TargetType.gateway;
    }
    return this.target_place !== null ? TargetType.place : TargetType.meter;
  }

  public get targetAll(): boolean {
    return this.target_meter === null && this.target_place === null;
  }

  public get targetMulti(): boolean {
    return this.target_meter === null;
  }

  public get targetEditType(): string {
    if (this.target_meter) return "meter";
    else if (this.target_place) return "place";
    return "all";
  }

  public get ruleType(): RuleType {
    let ruleType = null;

    switch (this.type) {
      case AlertType.burst_3p:
      case AlertType.leak_3p:
      case AlertType.t_from_pause_30_min:
      case AlertType.t_from_pause_60_min:
        ruleType = RuleType.leak;
        break;
      case AlertType.daily_profile:
      case AlertType.flow_profile:
        ruleType = RuleType.smart;
        break;
      default:
        ruleType = RuleType[this.type];
        break;
    }
    return ruleType;
  }

  public get ruleTargetMeter(): string {
    let ruleTargetMeter = null;

    switch (this.type) {
      case AlertType.volume:
      case AlertType.duration:
      case AlertType.daily_volume:
      case AlertType.monthly_volume:
        ruleTargetMeter = this.target_meter;
        break;
      default:
        ruleTargetMeter = null;
        break;
    }

    return ruleTargetMeter;
  }

  public get ruleTargetPlace(): string {
    let ruleTargetPlace = null;

    switch (this.type) {
      case AlertType.volume:
      case AlertType.duration:
      case AlertType.daily_volume:
      case AlertType.monthly_volume:
        ruleTargetPlace = this.target_place;
        break;
      default:
        ruleTargetPlace = null;
        break;
    }

    return ruleTargetPlace;
  }

  public get ruleId(): number {
    let ruleId = null;

    switch (this.type) {
      case AlertType.burst_3p:
      case AlertType.leak_3p:
      case AlertType.t_from_pause_30_min:
      case AlertType.t_from_pause_60_min:
        ruleId = LEAK_RULE_ID;
        break;
      case AlertType.daily_profile:
      case AlertType.flow_profile:
        ruleId = SMART_RULE_ID;
        break;
      case AlertType.periodicity:
        ruleId = PERIODICITY_RULE_ID;
        break;
      default:
        ruleId = this.id;
        break;
    }

    return ruleId;
  }

  public get sensibility(): number {
    if (this.high_value !== null && this.low_value !== null) {
      return this._convertValueToSensibility(
        this.value,
        this.low_value,
        this.high_value
      );
    }
    return 0;
  }

  private _convertValueToSensibility(
    x,
    inMin,
    inMax,
    outMin = 20,
    outMax = 100,
    step = 20
  ) {
    // Normalisation [0..1]
    let ratio = (x - inMin) / (inMax - inMin);
    ratio = Math.min(Math.max(ratio, 0), 1);

    // Mapping linéaire inversé
    let value = outMax - ratio * (outMax - outMin);

    // Arrondi au multiple de step
    return Math.round(value / step) * step;
  }

  constructor(
    public id: number,
    public type: AlertType,
    public label: string,
    public target_meter: string, // meter uuid
    public target_place: number, // place id
    public enabled: boolean,
    public value: number,
    public email_notif_enabled: boolean,
    public low_value: number, // for edit param (sensibility of smart alert, ...)
    public high_value: number, // for edit param (sensibility of smart alert, ...)
    public created_at: DateTime,
    public updated_at: DateTime
  ) {}

  public clone(): AlertDefinition {
    const clone = new AlertDefinition(
      this.id,
      this.type,
      this.label,
      this.target_meter,
      this.target_place,
      this.enabled,
      this.value,
      this.email_notif_enabled,
      this.low_value,
      this.high_value,
      this.created_at,
      this.updated_at
    );

    clone.targetLabel = this.targetLabel;
    clone.ruleLabel = this.ruleLabel;

    return clone;
  }

  public setData(data: AlertDefinitionData) {
    this.targetLabel = data.targetLabel;
    this.ruleLabel = data.ruleLabel;
  }

  public buildRule(): AlertRule {
    return new AlertRule(
      this.ruleId,
      this.ruleType,
      this.target_meter,
      this.targetLabel
    );
  }

  public getRuleLabel(user: User, utils: UtilsService): string {
    let ruleLabel = "-";

    let volume;
    switch (this.type) {
      case AlertType.duration:
        const duration = utils.getDurationFormatted(this.value, 2, true, false);
        ruleLabel = $localize`:@@alert.model.durationRuleLabel:Continuous water flow > ${duration}`;
        break;
      case AlertType.volume:
        volume = utils.getVolumeFormattedWithUser(user, this.value);
        ruleLabel = $localize`:@@alert.model.volumeRuleLabel:Continuous water flow > ${volume}`;
        break;
      case AlertType.daily_volume:
        volume = utils.getVolumeFormattedWithUser(user, this.value);
        ruleLabel = $localize`:@@alert.model.dailyVolumeRuleLabel:24-hour volume > ${volume}`;
        break;
      case AlertType.monthly_volume:
        volume = utils.getVolumeFormattedWithUser(user, this.value);
        ruleLabel = $localize`:@@alert.model.monthlyVolumeRuleLabel:30-day volume > ${volume}`;
        break;
      default:
        break;
    }

    return ruleLabel;
  }

  public toJSON(): AlertDefinition {
    return {
      id: this.id,
      type: this.type,
      label: this.label,
      target_meter: this.target_meter,
      target_place: this.target_place,
      enabled: this.enabled,
      value: this.value,
      email_notif_enabled: this.email_notif_enabled,
      // low_value: this.low_value,
      // high_value: this.high_value,
      // created_at: this.created_at,
      // updated_at: this.updated_at,
    } as AlertDefinition;
  }
}

@Injectable({
  providedIn: "root",
})
export class AlertDefinitionAdapter implements Adapter<AlertDefinition> {
  adapt(item: any): AlertDefinition {
    return new AlertDefinition(
      item.id,
      item.type,
      item.label,
      item.target_meter,
      item.target_place,
      item.enabled == 1 ? true : false,
      item.value,
      item.email_notif_enabled == 1 ? true : false,
      item.low_value,
      item.high_value,
      item.created_at
        ? DateTime.fromISO(item.created_at, { zone: Settings.defaultZone })
        : null,
      item.updated_at
        ? DateTime.fromISO(item.updated_at, { zone: Settings.defaultZone })
        : null
    );
  }
}

export class AlertRule implements Model {
  public definitions: AlertDefinition[] = [];
  public alerts: Alert[] = [];

  public get partiallyDisabled(): boolean {
    // partiellement désactivé
    if (!Array.isArray(this.definitions) || this.definitions.length === 0)
      return false;
    const disabledDefs = this.definitions.filter(
      (def) => def.enabled === false
    );
    return (
      disabledDefs.length > 0 && disabledDefs.length < this.definitions.length
    );
  }

  public get disabled(): boolean {
    // désactivé
    if (!Array.isArray(this.definitions) || this.definitions.length === 0)
      return false;
    const disabledDefs = this.definitions.filter(
      (def) => def.enabled === false
    );
    return (
      disabledDefs.length > 0 && disabledDefs.length === this.definitions.length
    );
  }

  public isDisabled(deviceUuid: string): boolean {
    if (
      deviceUuid === null ||
      !Array.isArray(this.definitions) ||
      this.definitions.length === 0
    )
      return this.disabled;

    // search definition for this device
    const defs = this.definitions.filter(
      (def) => def.target_meter === deviceUuid
    );
    if (defs.length > 0) return defs[0].enabled === false;

    return this.disabled;
  }

  public isNotNotified(deviceUuid: string): boolean {
    if (
      deviceUuid === null ||
      !Array.isArray(this.definitions) ||
      this.definitions.length === 0
    )
      return this.notNotified;

    const defs = this.definitions.filter(
      (def) => def.target_meter === deviceUuid
    );
    if (defs.length > 0) return defs[0].email_notif_enabled === false;

    return this.notNotified;
  }

  public get partiallyNotified(): boolean {
    // notification = email (partiellement)
    if (!Array.isArray(this.definitions) || this.definitions.length === 0)
      return false;
    const notifiedDefs = this.definitions.filter(
      (def) => def.email_notif_enabled === true
    );
    return (
      notifiedDefs.length > 0 && notifiedDefs.length < this.definitions.length
    );
  }

  public get notNotified(): boolean {
    // notification = email
    if (!Array.isArray(this.definitions) || this.definitions.length === 0)
      return false;
    const notNotifiedDefs = this.definitions.filter(
      (def) => def.email_notif_enabled === false
    );
    return (
      notNotifiedDefs.length > 0 &&
      notNotifiedDefs.length === this.definitions.length
    );
  }

  public get notified(): boolean {
    // notification = email
    if (!Array.isArray(this.definitions) || this.definitions.length === 0)
      return false;
    const notifiedDefs = this.definitions.filter(
      (def) => def.email_notif_enabled === true
    );
    return (
      notifiedDefs.length > 0 && notifiedDefs.length === this.definitions.length
    );
  }

  public get activeAlerts(): Alert[] {
    if (!Array.isArray(this.alerts) || this.alerts.length === 0) return [];

    const actives = this.alerts.filter((alert) => alert.is_active === true);

    return actives;
  }

  public get recentInactiveAlerts(): Alert[] {
    if (!Array.isArray(this.alerts) || this.alerts.length === 0) return [];
    const now = DateTime.local().toMillis();
    const threshold = 60 * 60 * 24 * 30; //TODO recent = 1 month ???

    const recentInactives = this.alerts.filter(
      (alert) =>
        alert.is_active === false &&
        (now - alert.end_date.toMillis()) / 1000 / 60 < threshold
    );

    return recentInactives;
  }

  public get category(): RuleCategory {
    let category = null;

    switch (this.type) {
      case RuleType.leak:
      case RuleType.periodicity:
        category = RuleCategory.leak;
        break;
      case RuleType.smart:
        category = RuleCategory.smart;
        break;
      case RuleType.gateway_offline:
      case RuleType.meter_offline:
      case RuleType.low_capacitor:
        category = RuleCategory.equipment;
        break;
      case RuleType.duration:
      case RuleType.volume:
        category = RuleCategory.threshold;
        break;
      case RuleType.daily_volume:
      case RuleType.monthly_volume:
        category = RuleCategory.period;
        break;
    }
    return category;
  }

  public get categoryLabel(): string {
    let label = null;

    switch (this.category) {
      case RuleCategory.smart:
        label = $localize`:@@alert.model.ruleCategorySmart:smart`;
        break;
      case RuleCategory.period:
        label = $localize`:@@alert.model.ruleCategoryPeriod:period`;
        break;
      case RuleCategory.equipment:
        label = $localize`:@@alert.model.ruleCategoryEquipment:device`;
        break;
      case RuleCategory.leak:
        label = $localize`:@@alert.model.ruleCategoryLeak:leak`;
        break;
      case RuleCategory.threshold:
        label = $localize`:@@alert.model.ruleCategoryThreshold:threshold`;
        break;
    }
    return label;
  }

  public get canEdit(): boolean {
    switch (this.type) {
      case RuleType.daily_volume:
      case RuleType.monthly_volume:
      case RuleType.duration:
      case RuleType.volume:
        return true;
    }
    return false;
  }

  public get canConfigure(): boolean {
    let result = true;
    if (this.canDelete || !this.canDisable) result = false;

    return result;
  }

  public get canDisable(): boolean {
    // return this.category !== RuleCategory.equipment;
    return true;
  }

  public get canDelete(): boolean {
    switch (this.type) {
      case RuleType.daily_volume:
      case RuleType.monthly_volume:
      case RuleType.duration:
      case RuleType.volume:
        return true;
    }
    return false;
  }

  public get label(): string {
    let label = "-";

    switch (this.type) {
      case RuleType.leak:
        label = $localize`:@@alert.model.leakRuleLabel:Leak detection`;
        break;
      case RuleType.periodicity:
        label = $localize`:@@alert.model.periodicityRuleLabel:Intermittent leak detection`;
        break;
      case RuleType.gateway_offline:
        label = $localize`:@@alert.model.offlineGatewayRuleLabel:Offline gateway`;
        break;
      case RuleType.meter_offline:
        label = $localize`:@@alert.model.offlineMeterRuleLabel:Offline meter`;
        break;
      case RuleType.low_capacitor:
        label = $localize`:@@alert.model.lowCapacitorRuleLabel:Low capacitor meter`;
        break;
      case RuleType.smart:
        label = $localize`:@@alert.model.smartRuleLabel:Consumption > typical day`; // Unusual consumption
      case RuleType.daily_volume:
      case RuleType.monthly_volume:
      case RuleType.duration:
      case RuleType.volume:
        const def = this.definitions.length === 1 ? this.definitions[0] : null;
        if (def) {
          label = def.ruleLabel;
        }
        break;
    }
    return label;
  }

  public get description(): string {
    let desc = null;

    switch (this.type) {
      case RuleType.daily_volume:
      case RuleType.monthly_volume:
      case RuleType.duration:
      case RuleType.volume:
        const def = this.definitions.length === 1 ? this.definitions[0] : null;
        if (def && def.label !== null && def.label.trim().length > 0) {
          desc = def.label;
        }
        break;
      default:
        break;
    }

    return desc;
  }

  public get targetLabel(): string {
    let targetLabel = null;

    if (this.targetAll) {
      if (this.type === RuleType.gateway_offline) {
        targetLabel = $localize`:@@alert.model.targetAllLoraGateways:All (LoRa)`;
      } else if (
        this.type === RuleType.smart ||
        this.type === RuleType.periodicity
      ) {
        targetLabel = $localize`:@@alert.model.targetAllDracMeters:All (Drac)`;
      } else {
        targetLabel = $localize`:@@alert.model.targetAllMeters:All`;
      }
    } else {
      const def = this.definitions.length === 1 ? this.definitions[0] : null;
      if (def) {
        targetLabel = def.targetLabel;
      }
    }

    return targetLabel;
  }

  public get targetDracOnly(): boolean {
    return dracRuleTypes.indexOf(this.type) !== -1;
  }

  public get notificationLabel(): string {
    if (this.partiallyNotified)
      return $localize`:@@alert.model.notifPartiallyDisabled:Notification (by target)`;
    else if (this.notified)
      return $localize`:@@alert.model.notifEnabled:Notification enabled`;
    else return $localize`:@@alert.model.notifDisabled:Notification disabled`;
  }

  public get targetType(): TargetType {
    if (this.type === RuleType.gateway_offline) {
      return TargetType.gateway;
    }
    return this.target_place !== null ? TargetType.place : TargetType.meter;
  }

  public get targetAll(): boolean {
    return this.target_meter === null && this.target_place === null;
  }

  public get targetMulti(): boolean {
    return this.target_meter === null;
  }

  public get targetPlaceId(): number {
    switch (this.type) {
      case RuleType.daily_volume:
      case RuleType.monthly_volume:
      case RuleType.duration:
      case RuleType.volume:
        return this.definitions.length === 1
          ? this.definitions[0].target_place
          : null;

      default:
        return null;
    }
  }

  public get ruleLabel(): string {
    let label = "-";

    switch (this.type) {
      case RuleType.leak:
      case RuleType.periodicity:
      case RuleType.gateway_offline:
      case RuleType.meter_offline:
      case RuleType.low_capacitor:
      case RuleType.smart:
        label = null;
        break;
      case RuleType.daily_volume:
      case RuleType.monthly_volume:
      case RuleType.duration:
      case RuleType.volume:
        const def = this.definitions.length === 1 ? this.definitions[0] : null;
        if (def) {
          if (def.label !== null && def.label.trim().length > 0)
            label = def.ruleLabel;
          else label = null;
        }
        break;
    }
    return label;
  }

  constructor(
    public id: number,
    public type: RuleType,
    public target_meter: string,
    public target_place: string
  ) {}

  public clone(): AlertRule {
    const clone = new AlertRule(
      this.id,
      this.type,
      this.target_meter,
      this.target_place
    );

    clone.definitions = this.definitions;
    clone.alerts = this.alerts;

    return clone;
  }

  public getSummary(user: User, utils: UtilsService) {
    let summary = null;

    switch (this.type) {
      case RuleType.daily_volume:
        summary = $localize`:@@alert.model.dailyVolumeSummary:Triggers if your water use goes over the threshold you set during any continuous 24-hour period. Useful for keeping your water usage in check and spotting overages.`;
        break;
      case RuleType.monthly_volume:
        summary = $localize`:@@alert.model.monthlyVolumeSummary:Triggers if your water use goes over the threshold you set during any continuous 30-day period. Useful for keeping your water usage in check and spotting overages.`;
        break;
      case RuleType.duration:
        summary = $localize`:@@alert.model.durationSummary:Triggers if water flows continuously and exceeds the duration limit you set. Useful to detect a forgotten tap or a leak.`;
        break;
      case RuleType.volume:
        summary = $localize`:@@alert.model.volumeSummary:Triggers if water flows continuously and exceeds the volume limit you set. Useful to detect a forgotten tap or a leak.`;
        break;
      case RuleType.gateway_offline:
        summary = $localize`:@@alert.model.gatewayOfflineSummary:Triggers if no communication from the gateway is detected. The issue may be with the gateway or the network. Try restarting your internet router or the gateway.`;
        break;
      case RuleType.meter_offline:
        summary = $localize`:@@alert.model.meterOfflineSummary:Triggers if no data is received from the meter for over 2 days. This may indicate a connection issue or a non-functioning meter.`;
        break;
      case RuleType.low_capacitor:
        summary = $localize`:@@alert.model.lowCapacitorSummary:Triggers if the meter's energy level is too low. This may lead to interruptions in data transmission.`;
        break;
      case RuleType.periodicity:
        summary = $localize`:@@alert.model.periodicitySummary:Automatically detects a series of water flows regularly spaced, often caused by a toilet flush that doesn’t shut off properly. An estimate of hourly water loss is provided if the alert is triggered.`;
        break;
      case RuleType.leak:
        // summary = $localize`:@@alert.model.tFromPauseSummary:Automatically triggers when no period without water use has been detected for a long time. Useful to spot a leak or near-continuous consumption. Less reactive than other alerts and may take longer to activate.`;
        // summary = $localize`:@@alert.model.leak3pSummary:Alert generated and sent directly by the meter when it detects continuous water use without sufficient breaks. May indicate a leak, even a small one.`;
        // summary = $localize`:@@alert.model.burst3pSummary:Generated and sent directly by the meter when it detects unusually high flow. May indicate a pipe burst or accidental overuse.`;
        summary = $localize`:@@alert.model.leakSummary:Triggers when continuous water use without sufficient breaks, or unusually high flow, is detected. May indicate a leak, a burst pipe, or accidental overuse.`;
        break;
      case RuleType.smart:
        // summary = $localize`:@@alert.model.dailyProfileSummary:Triggers if your total daily water use time is significantly higher than usual. It helps you spot unusual shifts in your daily routine. You can adjust the sensitivity to detect smaller or larger changes.`;
        // summary = $localize`:@@alert.model.flowProfileSummary:Notifies you to unusual changes in flow rates, such as the appearance of a new flow or a sharp increase on a familiar one. Helps quickly detect unexpected usage. Sensitivity can be set to filter minor fluctuations or only the major ones.`;
        summary = $localize`:@@alert.model.smartProfileSumary:Triggers if unusual shifts in your daily routine occur, such as changes in your total daily water usage or flow rates. You can adjust the sensitivity to detect smaller or larger changes.`;
        break;
    }

    return summary;
  }
}

export class AlertRuleDevice implements Model {
  public get canEdit(): boolean {
    return (
      Array.isArray(this.alert_definitions) && this.alert_definitions.length > 0
    );
  }

  public get label(): string {
    if (!this.device) return undefined;

    return this.device_type === "meter"
      ? (this.device as Meter).universalLabel
      : (this.device as Gateway).universalLabel;
  }

  public get placeLabel(): string | undefined {
    if (!this.device) return undefined;

    return this.device_type === "meter"
      ? (this.device as Meter).placeLabel
      : null;
  }

  public get uuid(): string | undefined {
    if (!this.device) return undefined;

    return this.device_type === "meter"
      ? (this.device as Meter).device_uuid
      : (this.device as Gateway).gateway_uuid;
  }

  public get sensibility(): number {
    const def =
      this.alert_definitions?.length > 0 ? this.alert_definitions[0] : null;
    if (!def || !def.sensibility) return null;

    // convert 20-100 to 0-100 for view
    return ((def.sensibility - 20) * (100 - 0)) / (100 - 20) + 0;
  }

  constructor(
    public device_type: string,
    public device: Gateway | Meter,
    public is_compatible: boolean,
    public alert_definitions: AlertDefinition[],
    public notified: boolean,
    public enabled: boolean
  ) {}

  public clone(): AlertRuleDevice {
    const clone = new AlertRuleDevice(
      this.device_type,
      this.device,
      this.is_compatible,
      this.alert_definitions,
      this.notified,
      this.enabled
    );

    return clone;
  }
}
